/*
  Sales & Stock Progress (PDP block)
  ----------------------------------
  Turns four per-product metafields into two clock-driven counters:

    sold  = min(sold_start + ticks_since_reset, sold_max)
    stock = max(stock_start - ticks_since_reset, stock_min)

  Both counters hang off the SAME daily reset, and both are MONOTONIC between
  resets: sold only ever rises, stock only ever falls. That is the whole point
  of the shape. An earlier version looped the stock counter
  (`start - (ticks % span)`), which meant a shopper who stayed on the page long
  enough watched "Only 3 left" climb back to "Only 10 left" — a claim the page
  had just contradicted itself on. A number that can go up is worse than no
  number at all, so stock now holds at its floor until the next reset, exactly
  the way sold holds at its ceiling.

  The two run on separate intervals because they describe different things.
  Sold is a busy-day signal and should move while someone is deciding; stock is
  a scarcity signal and must stay believable across a whole day. Liquid sends
  `data-stock-interval` already sized so the full stock_start -> stock_minimum
  journey spans the day rather than an afternoon.

  Why this runs in JS at all. Shopify caches product HTML, so a Liquid-computed
  `now` is baked into the cache and goes stale by minutes to hours. Every
  time-dependent value in this theme is therefore client side - see
  `snippets/countdown-timer.liquid`, which renders the literal placeholder "00"
  and lets `countdown-timer.js` fill it. Liquid here renders the *seed* values,
  which are static metafields and so cache-safe; this file refines them.

  Why there is no storage and no timer. The displayed number is a pure function
  of (clock, metafields, product id), so two shoppers on two devices see the
  same value with nothing persisted, and a returning shopper is never
  contradicted by a cleared cache. localStorage would also be wrong here in
  practice: `isStorageSupported()` in theme-global.js returns false inside an
  iframe, which is exactly the theme editor preview.

  Time is computed in SHOP time, not browser time, so the merchant's "reset at
  12:00 AM" means midnight in the store's timezone for every shopper on earth.
  The offset arrives as `data-tz-offset` from Liquid's `%z`. Known limit: that
  offset is baked into cached HTML, so across a daylight-saving change the reset
  hour can be an hour off until the cache turns over. Liquid exposes no IANA
  zone name, so there is no better source available.
*/
if (!customElements.get("m-sales-progress")) {
  const MS_IN_MIN = 60000;
  const MS_IN_DAY = 86400000;

  customElements.define(
    "m-sales-progress",
    class MSalesProgress extends HTMLElement {
      connectedCallback() {
        this.soldEl = this.querySelector("[data-sp-sold]");
        this.stockEl = this.querySelector("[data-sp-stock]");
        if (!this.soldEl && !this.stockEl) return;

        const d = this.dataset;
        this.interval = Math.max(1, parseInt(d.interval, 10) || 1);
        this.stockInterval = Math.max(1, parseInt(d.stockInterval, 10) || 1);
        this.resetMinutes = parseInt(d.resetMinutes, 10) || 0;
        this.offsetMs = (parseInt(d.tzOffset, 10) || 0) * MS_IN_MIN;
        // A stable per-product phase so two products with identical metafields
        // do not step in the same second — without it a shopper moving between
        // two products sees both counters flip together, which reads as one
        // script driving both rather than two independent products. Precedent:
        // saleProgress() in theme-global.js derives a per-product ratio from
        // the product id. Each counter takes the phase modulo ITS OWN interval,
        // so the offset is always a fraction of a step and can never push a
        // counter past a whole extra unit.
        const seed = this.hash(d.productId || "");
        this.phase = seed % this.interval;
        this.stockPhase = seed % this.stockInterval;

        this.render = this.render.bind(this);
        this.onVisible = this.onVisible.bind(this);

        this.render();
        // Recompute when the page is looked at again rather than polling for
        // the moment it changes. pageshow also covers a bfcache restore, where
        // no other event fires and the DOM can be hours old.
        document.addEventListener("visibilitychange", this.onVisible);
        window.addEventListener("pageshow", this.render);
      }

      disconnectedCallback() {
        document.removeEventListener("visibilitychange", this.onVisible);
        window.removeEventListener("pageshow", this.render);
      }

      onVisible() {
        if (!document.hidden) this.render();
      }

      /* Small string hash (djb2-xor). Only needs to spread ids, not resist attack. */
      hash(str) {
        let h = 5381;
        for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
        return Math.abs(h);
      }

      /*
        Minutes elapsed since the most recent occurrence of the merchant's reset
        time, in shop time. Before today's reset has happened the current run
        began yesterday, so the anchor steps back a day.

        Both counters share this anchor, which is what keeps them telling one
        story: at the reset hour the day starts over for sold and for stock
        together, and in between neither can contradict the other.
      */
      minutesSinceReset(nowShop) {
        let resetAt = Math.floor(nowShop / MS_IN_DAY) * MS_IN_DAY + this.resetMinutes * MS_IN_MIN;
        if (nowShop < resetAt) resetAt -= MS_IN_DAY;
        return (nowShop - resetAt) / MS_IN_MIN;
      }

      render() {
        const elapsed = this.minutesSinceReset(Date.now() + this.offsetMs);

        if (this.soldEl) {
          const start = Number(this.soldEl.dataset.spStart);
          const max = Number(this.soldEl.dataset.spMax);
          const ticks = Math.floor((elapsed + this.phase) / this.interval);
          // Holds at the ceiling until the next reset rather than overshooting.
          this.paint(this.soldEl, Math.min(start + ticks, max), max);
        }

        if (this.stockEl) {
          const start = Number(this.stockEl.dataset.spStart);
          const min = Number(this.stockEl.dataset.spMin);
          const ticks = Math.floor((elapsed + this.stockPhase) / this.stockInterval);
          // Mirror image of sold: falls one unit per interval and then holds at
          // the floor until the reset. Never rises inside a day, so the number
          // a shopper reads is the lowest they have been shown this session.
          this.paint(this.stockEl, Math.max(start - ticks, min), start);
        }

        // Gate the bar animation on the real value: the stylesheet holds the
        // fill at animation:none until `is-ready`, so it travels once, to the
        // final number, instead of racing the seed value Liquid rendered.
        //
        // On a re-render (tab refocus) the animation has already resolved its
        // `to` frame and fill-mode:both is holding it, so a new --sp-pct would
        // not move the bar. Dropping the class and forcing a reflow restarts
        // it against the new value. That reflow costs one layout, and only on
        // a visibility change - never on a timer.
        if (this.classList.contains("is-ready")) {
          this.classList.remove("is-ready");
          void this.offsetWidth;
        }
        this.classList.add("is-ready");
      }

      paint(item, count, denominator) {
        const label = item.querySelector(".m-sales-progress__label");
        // [count], never a brace pair - a literal }} inside a Liquid output tag
        // terminates it even inside quotes and takes the whole section down.
        if (label) label.textContent = (item.dataset.spLabel || "").replace("[count]", count);
        const pct = denominator > 0 ? Math.min(100, Math.max(0, (count * 100) / denominator)) : 0;
        item.style.setProperty("--sp-pct", pct);
      }
    }
  );
}
